/**
 * ProjectsPage - Saved Carousel Projects
 *
 * Lists the user's saved carousel projects with search, filters, and
 * grid/list views. Supports renaming, duplicating, and deleting projects,
 * and navigating into the editor.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { listProjects, deleteProject, duplicateProject, renameProject, ProjectSummary } from '@/services/projectService';
import { apiPost } from '@/services/apiClient';
import { getUsageStats, UsageStats } from '@/services/trackingService';
import { getProfile, ProfilePreferences, UserProfile } from '@/services/profileService';
import CoreStudioMark from '@/components/ui/core-studio-mark';
import InstagramProfileModal from '@/components/InstagramProfileModal';
import AvatarOnboardingModal from '@/components/AvatarOnboardingModal';
import LoadingScreen from '@/components/ui/loading-screen';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { PROFILE_UPDATED_EVENT } from '@/lib/displayName';
import {
  Plus, Search, Trash2, Copy,
  BarChart3, FileText, Loader2, Instagram, Users,
  LayoutGrid, List, Calendar, Pencil, Check, X
} from 'lucide-react';

// ============================================================================
// TYPES & HELPERS
// ============================================================================

type ViewMode = 'grid' | 'list';
type PeriodFilter = 'ALL' | 'TODAY' | 'WEEK' | 'MONTH' | '3MONTHS' | 'YEAR';
type StyleFilter = 'ALL' | 'TWITTER' | 'STORYTELLER';

const PERIOD_KEYS: Record<PeriodFilter, string> = {
  ALL: 'periodAll',
  TODAY: 'periodToday',
  WEEK: 'periodWeek',
  MONTH: 'periodMonth',
  '3MONTHS': 'period3Months',
  YEAR: 'periodYear',
};

const STYLE_KEYS: Record<StyleFilter, string> = {
  ALL: 'styleAll',
  TWITTER: 'styleTwitter',
  STORYTELLER: 'styleStoryteller',
};

/**
 * Checks if a date string falls within the given period relative to now.
 */
function isWithinPeriod(dateStr: string, period: PeriodFilter): boolean {
  if (period === 'ALL') return true;

  const date = new Date(dateStr);
  const now = new Date();

  switch (period) {
    case 'TODAY': {
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return date >= start;
    }
    case 'WEEK': {
      const dayOfWeek = now.getDay();
      const diffToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diffToMonday);
      return date >= start;
    }
    case 'MONTH': {
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      return date >= start;
    }
    case '3MONTHS': {
      const start = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
      return date >= start;
    }
    case 'YEAR': {
      const start = new Date(now.getFullYear(), 0, 1);
      return date >= start;
    }
    default:
      return true;
  }
}

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const { user } = useAuth();
  const { status, isPaywalled, setShowPaywall, refreshStatus } = useUserStatus();
  const { notifyDashboardReady } = useOnboarding();

  const locale = language === 'pt-BR' ? 'pt-BR' : language === 'es' ? 'es' : 'en';
  const periodLabels = useMemo(() => (
    Object.fromEntries(
      (Object.keys(PERIOD_KEYS) as PeriodFilter[]).map((k) => [k, t(`dashboard.${PERIOD_KEYS[k]}`)])
    ) as Record<PeriodFilter, string>
  ), [t]);
  const styleLabels = useMemo(() => (
    Object.fromEntries(
      (Object.keys(STYLE_KEYS) as StyleFilter[]).map((k) => [k, t(`dashboard.${STYLE_KEYS[k]}`)])
    ) as Record<StyleFilter, string>
  ), [t]);

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [stats, setStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renamingValue, setRenamingValue] = useState('');
  const [renamingSavingId, setRenamingSavingId] = useState<string | null>(null);

  // View mode (persisted in localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try { return (localStorage.getItem('dashboard_viewMode') as ViewMode) || 'grid'; }
    catch { return 'grid'; }
  });

  // Filters
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('ALL');
  const [styleFilter, setStyleFilter] = useState<StyleFilter>('ALL');

  // Instagram profile modal
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileRequired, setProfileRequired] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userPreferences, setUserPreferences] = useState<ProfilePreferences | null>(null);

  // Avatar onboarding modal
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarRequired, setAvatarRequired] = useState(false);

  // Show paywall modal when a paywalled user arrives at the dashboard.
  // Deferred while loading or during onboarding (profile/avatar setup)
  // so the onboarding flow is never interrupted.
  useEffect(() => {
    // Don't show paywall while dashboard is still loading or during onboarding
    if (loading || profileRequired || avatarRequired) return;

    if (isPaywalled) {
      setShowPaywall(true);
      // Sync paywall_active in DB if not yet set (fire-and-forget)
      if (status?.trialUsed && !status?.paywallActive && status?.plan === 'free') {
        apiPost('/api/user/activate-paywall', {}).catch(() => {});
      }
    }
    // Clean up ?paywall=1 param if present
    if (searchParams.get('paywall') === '1') {
      searchParams.delete('paywall');
      setSearchParams(searchParams, { replace: true });
    }
  }, [isPaywalled, status, searchParams, setShowPaywall, setSearchParams, loading, profileRequired, avatarRequired]);

  // Persist view mode
  useEffect(() => {
    try { localStorage.setItem('dashboard_viewMode', viewMode); } catch { }
  }, [viewMode]);

  // Load projects, stats, and check profile
  useEffect(() => {
    const loadData = async () => {
      try {
        if (!user) return;

        // Refresh trial/paywall status in parallel with other fetches.
        // This is where the frontend learns about trial consumption
        // (CreatePage no longer calls refreshStatus to avoid premature paywall).
        // Use Promise.allSettled so a single failure (e.g. listProjects on a
        // brand-new account) doesn't prevent the profile/avatar modals from
        // showing — those are critical for the onboarding flow.
        const [, projectsResult, statsResult, profileResult] = await Promise.allSettled([
          refreshStatus(),
          listProjects(),
          getUsageStats(
            new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // Start of month
            new Date().toISOString().split('T')[0] // Today
          ),
          getProfile(user.id),
        ]);

        const projectList = projectsResult.status === 'fulfilled' ? projectsResult.value : [];
        const usageStats = statsResult.status === 'fulfilled' ? statsResult.value : null;
        const profile = profileResult.status === 'fulfilled' ? profileResult.value : null;

        setProjects(projectList);
        setStats(usageStats);
        setUserProfile(profile);
        setUserPreferences(profile?.preferences || null);

        // Show profile modal if handle is not set
        if (!profile || !profile.handle || !profile.handle.trim()) {
          setProfileRequired(true);
          setShowProfileModal(true);
        } else if (!(profile?.preferences?.avatarData)) {
          // Profile exists but avatar not defined -> show avatar onboarding
          setAvatarRequired(true);
          setShowAvatarModal(true);
        } else {
          // No modals needed — dashboard is ready for onboarding tour
          notifyDashboardReady();
        }
      } catch (error) {
        // Failed to load dashboard data - handled by loading state
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, refreshStatus]);

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Tem certeza que deseja excluir este projeto?')) return;

    setDeletingId(projectId);
    try {
      await deleteProject(projectId);
      setProjects(prev => prev.filter(p => p.id !== projectId));
    } catch (error) {
        // Failed to delete project - alert shown to user
      alert(t('dashboard.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicateProject = async (projectId: string) => {
    if (!user) return;

    setDuplicatingId(projectId);
    try {
      const newId = await duplicateProject(projectId, user.id);
      const updatedProjects = await listProjects();
      setProjects(updatedProjects);
      navigate(`/editor/${newId}`);
    } catch (error) {
        // Failed to duplicate project - alert shown to user
      alert(t('dashboard.duplicateFailed'));
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleStartRename = (project: ProjectSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingId(project.id);
    setRenamingValue(project.name);
  };

  const handleCancelRename = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setRenamingId(null);
    setRenamingValue('');
    setRenamingSavingId(null);
  };

  const handleConfirmRename = async (project: ProjectSummary, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const trimmedName = renamingValue.trim();

    if (!trimmedName) {
      alert(t('dashboard.renameEmpty'));
      return;
    }

    if (trimmedName === project.name) {
      handleCancelRename();
      return;
    }

    setRenamingSavingId(project.id);
    try {
      await renameProject(project.id, trimmedName);
      setProjects((prev) => prev.map((p) => (
        p.id === project.id ? { ...p, name: trimmedName } : p
      )));
      handleCancelRename();
    } catch (error) {
        // Failed to rename project - alert shown to user
      alert(t('dashboard.renameFailed'));
      setRenamingSavingId(null);
    }
  };

  // Filter projects by search query, period, and style
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStyle = styleFilter === 'ALL' || p.style === styleFilter;
    const matchesDate = isWithinPeriod(p.updatedAt, periodFilter);
    return matchesSearch && matchesStyle && matchesDate;
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStyleBadgeColor = (style: string) => {
    switch (style) {
      case 'TWITTER': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30';
      case 'STORYTELLER': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/30';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  if (loading) {
    return <LoadingScreen message={t('dashboard.loadingProjects')} />;
  }

  return (
    <div className="min-h-full bg-background">
      {/* Page Header */}
      <div className="px-4 md:px-8 pt-6 md:pt-8 pb-2">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-2 gap-3 md:gap-0">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground">
              {t('projects.title')}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {t('projects.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 w-full md:w-auto">
            {/* Instagram profile button - hidden on mobile */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setProfileRequired(false);
                setShowProfileModal(true);
              }}
              title={t('dashboard.instagramProfile')}
              className={cn(
                'hidden md:flex gap-2',
                !userProfile?.handle && 'border-orange-500/50 text-orange-500'
              )}
            >
              <Instagram className="h-4 w-4" />
              {!userProfile?.handle && <span>{t('dashboard.configureProfile')}</span>}
            </Button>

            {/* Avatar / Target Audience button - hidden on mobile */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAvatarRequired(false);
                setShowAvatarModal(true);
              }}
              title={t('dashboard.targetAudience')}
              className={cn(
                'hidden md:flex gap-2',
                !userPreferences?.avatarData && 'border-orange-500/50 text-orange-500'
              )}
            >
              <Users className="h-4 w-4" />
              {!userPreferences?.avatarData && <span>{t('dashboard.defineAudience')}</span>}
            </Button>

          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 py-4 md:py-6">
        {/* Action Bar */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 mb-6">
          {/* Search - full width on mobile */}
          <div className="relative w-full md:flex-1 md:min-w-[200px] md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('dashboard.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Filters row - horizontal scroll on mobile */}
          <div className="flex items-center gap-2 md:gap-3 overflow-x-auto no-scrollbar">
            {/* Period Filter */}
            <Select value={periodFilter} onValueChange={(v) => setPeriodFilter(v as PeriodFilter)}>
              <SelectTrigger className="w-[140px] md:w-[170px] flex-shrink-0">
                <Calendar className="h-3.5 w-3.5 mr-2 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(periodLabels) as PeriodFilter[]).map((key) => (
                  <SelectItem key={key} value={key}>{periodLabels[key]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Style Filter */}
            <Select value={styleFilter} onValueChange={(v) => setStyleFilter(v as StyleFilter)}>
              <SelectTrigger className="w-[140px] md:w-[170px] flex-shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(styleLabels) as StyleFilter[]).map((key) => (
                  <SelectItem key={key} value={key}>{styleLabels[key]}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View Mode Toggle */}
            <div className="flex bg-secondary rounded-lg p-1 ml-auto flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-8 rounded-md',
                  viewMode === 'grid' && 'bg-primary text-primary-foreground shadow-sm'
                )}
                onClick={() => setViewMode('grid')}
                title={t('dashboard.viewGrid')}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  'h-8 w-8 rounded-md',
                  viewMode === 'list' && 'bg-primary text-primary-foreground shadow-sm'
                )}
                onClick={() => setViewMode('list')}
                title={t('dashboard.viewList')}
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Active Filters Summary */}
        {(periodFilter !== 'ALL' || styleFilter !== 'ALL') && (
          <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
            <span>{t('dashboard.projectsFound', { count: filteredProjects.length })}</span>
            {periodFilter !== 'ALL' && (
              <span className="bg-primary/10 text-primary px-2 py-0.5 rounded-full">{periodLabels[periodFilter]}</span>
            )}
            {styleFilter !== 'ALL' && (
              <span className={cn("px-2 py-0.5 rounded-full border", getStyleBadgeColor(styleFilter))}>{styleLabels[styleFilter]}</span>
            )}
            <button
              onClick={() => { setPeriodFilter('ALL'); setStyleFilter('ALL'); }}
              className="text-primary hover:underline ml-1"
            >
              {t('dashboard.clearFilters')}
            </button>
          </div>
        )}

        {/* Projects Section */}
        <div>
        {filteredProjects.length === 0 ? (
          <Card className="p-6 md:p-12 text-center">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="h-8 w-8 text-primary/50" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">
                  {searchQuery || periodFilter !== 'ALL' || styleFilter !== 'ALL'
                    ? t('dashboard.noProjectsFiltered')
                    : t('dashboard.noProjects')}
                </h3>
                <p className="text-muted-foreground text-sm mt-1">
                  {searchQuery || periodFilter !== 'ALL' || styleFilter !== 'ALL'
                    ? t('dashboard.adjustFilters')
                    : t('dashboard.createFirst')}
                </p>
              </div>
              {!searchQuery && periodFilter === 'ALL' && styleFilter === 'ALL' && (
                <Button onClick={() => {
                  if (isPaywalled) {
                    setShowPaywall(true);
                    return;
                  }
                  navigate('/');
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('sidebar.newChat')}
                </Button>
              )}
            </div>
          </Card>
        ) : viewMode === 'grid' ? (
          /* ============ GRID VIEW ============ */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer border-border/50 hover:border-primary/30"
                onClick={() => navigate(`/editor/${project.id}`)}
              >
                {/* Thumbnail */}
                <div className="aspect-video bg-muted rounded-t-xl flex items-center justify-center overflow-hidden">
                  {project.thumbnailUrl ? (
                    <img
                      src={project.thumbnailUrl}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-muted-foreground">
                      <CoreStudioMark size={32} className="text-muted-foreground/50" useCurrentColor />
                      <span className="text-xs">{project.slideCount} {project.slideCount === 1 ? t('dashboard.slide') : t('dashboard.slides')}</span>
                    </div>
                  )}
                </div>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      {renamingId === project.id ? (
                        <Input
                          value={renamingValue}
                          onChange={(e) => setRenamingValue(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              void handleConfirmRename(project);
                            }
                            if (e.key === 'Escape') {
                              handleCancelRename();
                            }
                          }}
                          className="h-7 text-sm"
                          autoFocus
                          disabled={renamingSavingId === project.id}
                        />
                      ) : (
                        <h3 className="font-semibold text-sm truncate">{project.name}</h3>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(project.updatedAt)}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      {renamingId === project.id ? (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => void handleConfirmRename(project, e)}
                            disabled={renamingSavingId === project.id}
                            title="Salvar nome"
                          >
                            {renamingSavingId === project.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={handleCancelRename}
                            disabled={renamingSavingId === project.id}
                            title="Cancelar"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => handleStartRename(project, e)}
                          title="Renomear projeto"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                    <span className="text-xs text-muted-foreground">
                      {project.slideCount} {project.slideCount === 1 ? t('dashboard.slide') : t('dashboard.slides')} &middot; {project.aspectRatio}
                    </span>

                    <div className="flex items-center gap-2">
                      <span className={cn(
                        "text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0",
                        getStyleBadgeColor(project.style)
                      )}>
                        {project.style}
                      </span>
                      <div className="flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateProject(project.id);
                          }}
                          disabled={duplicatingId === project.id}
                          title="Duplicar"
                        >
                          {duplicatingId === project.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteProject(project.id);
                          }}
                          disabled={deletingId === project.id}
                          title="Excluir"
                        >
                          {deletingId === project.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          /* ============ LIST VIEW ============ */
          <div className="space-y-2">
            {/* List Header */}
            <div className="hidden md:grid grid-cols-[64px_1fr_100px_80px_140px_80px] gap-4 items-center px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <span></span>
              <span>Nome</span>
              <span>Estilo</span>
              <span>Slides</span>
              <span>Atualizado</span>
              <span className="text-right">Ações</span>
            </div>

            {filteredProjects.map((project) => (
              <Card
                key={project.id}
                className="group hover:shadow-md hover:shadow-primary/5 transition-all cursor-pointer border-border/50 hover:border-primary/30"
                onClick={() => navigate(`/editor/${project.id}`)}
              >
                <CardContent className="p-3 md:p-4">
                  <div className="grid grid-cols-[64px_1fr_auto] md:grid-cols-[64px_1fr_100px_80px_140px_80px] gap-3 md:gap-4 items-center">
                    {/* Thumbnail */}
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                      {project.thumbnailUrl ? (
                        <img
                          src={project.thumbnailUrl}
                          alt={project.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <CoreStudioMark size={20} className="text-muted-foreground/50" useCurrentColor />
                      )}
                    </div>

                    {/* Name + Date (mobile) */}
                    <div className="min-w-0 flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        {renamingId === project.id ? (
                          <Input
                            type="text"
                            value={renamingValue}
                            onChange={(e) => setRenamingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void handleConfirmRename(project);
                              if (e.key === 'Escape') handleCancelRename();
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 text-sm"
                            autoFocus
                            disabled={renamingSavingId === project.id}
                          />
                        ) : (
                          <h3 className="font-semibold text-sm truncate">{project.name}</h3>
                        )}
                        <p className="text-xs text-muted-foreground mt-0.5 md:hidden">
                          {formatDate(project.updatedAt)}
                        </p>
                      </div>
                      {renamingId === project.id && (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={(e) => void handleConfirmRename(project, e)}
                            disabled={renamingSavingId === project.id}
                            title="Salvar nome"
                          >
                            {renamingSavingId === project.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Check className="h-3.5 w-3.5" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={handleCancelRename}
                            disabled={renamingSavingId === project.id}
                            title="Cancelar"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Style Badge (desktop) */}
                    <div className="hidden md:block">
                      <span className={cn(
                        "text-[10px] font-semibold px-2 py-0.5 rounded-full border",
                        getStyleBadgeColor(project.style)
                      )}>
                        {project.style}
                      </span>
                    </div>

                    {/* Slide Count (desktop) */}
                    <span className="hidden md:block text-xs text-muted-foreground">
                      {project.slideCount} {project.slideCount === 1 ? t('dashboard.slide') : t('dashboard.slides')}
                    </span>

                    {/* Date (desktop) */}
                    <span className="hidden md:block text-xs text-muted-foreground">
                      {formatDate(project.updatedAt)}
                    </span>

                    {/* Actions */}
                    <div className="flex gap-1 justify-end">
                      {/* Mobile: style badge */}
                      <span className={cn(
                        "text-[10px] font-semibold px-2 py-0.5 rounded-full border md:hidden mr-1",
                        getStyleBadgeColor(project.style)
                      )}>
                        {project.style}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartRename(project, e);
                        }}
                        title="Renomear projeto"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDuplicateProject(project.id);
                        }}
                        disabled={duplicatingId === project.id}
                        title="Duplicar"
                      >
                        {duplicatingId === project.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 hover:text-destructive md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteProject(project.id);
                        }}
                        disabled={deletingId === project.id}
                        title="Excluir"
                      >
                        {deletingId === project.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* Instagram Profile Modal */}
      <InstagramProfileModal
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        onProfileSaved={(savedProfile) => {
          setUserProfile(savedProfile);
          setUserPreferences(savedProfile.preferences || null);
          window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
          setProfileRequired(false);
          // After profile saved, check if avatar is needed
          if (!(savedProfile.preferences?.avatarData || userPreferences?.avatarData)) {
            setAvatarRequired(true);
            setShowAvatarModal(true);
          } else {
            // Profile saved and avatar exists — dashboard ready for onboarding tour
            notifyDashboardReady();
          }
        }}
        required={profileRequired}
        userId={user?.id || ''}
      />

      {/* Avatar Onboarding Modal */}
      <AvatarOnboardingModal
        open={showAvatarModal}
        onClose={() => setShowAvatarModal(false)}
        onAvatarSaved={(avatar) => {
          setUserPreferences(prev => ({ ...prev, avatarData: avatar }));
          setAvatarRequired(false);
          // All setup modals complete — dashboard ready for onboarding tour
          notifyDashboardReady();
        }}
        required={avatarRequired}
        userId={user?.id || ''}
        initialData={userPreferences?.avatarData}
      />
    </div>
  );
};

export default ProjectsPage;
